import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import ChassisWithExternalDropdown from '../../../../components/common/ChassisWithExternalDropdown';
import CountryDropdown from '../../../../components/common/CountryDropdown';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { validateTCNumber } from '../../../../utils/validationUtils';
import { useGetCustomerTypesQuery } from '../../../../api/customerApi';

const CustomerDefinitionDialog = ({ visible, onHide, formData, setFormData, errors, handleSave, editMode, handleDelete, isSubmitting = false, isDeleting = false, prefill, addChassis, removeChassis }) => {
    const { th , getCurrentLanguageId } = useLanguage();
    const toast = useRef(null);
    const [touched, setTouched] = useState({});
    const [formSubmitted, setFormSubmitted] = useState({});

    const languageId = getCurrentLanguageId();

    const { data: customerTypesData, isSuccess: isCustomerTypesSuccess } =
        useGetCustomerTypesQuery(languageId, {
            skip: !visible || !languageId
        });

    const enableCustomerTypeFeatures =
        isCustomerTypesSuccess &&
        customerTypesData &&
        Array.isArray(customerTypesData) &&
        customerTypesData.length > 0;

    const customerTypes = enableCustomerTypeFeatures ? customerTypesData : [];

    React.useEffect(() => {
        if (visible) {
            setTouched({});
            setFormSubmitted(false);
        }
    }, [visible]);

    React.useEffect(() => {
        if (!visible || editMode || !prefill) return;
        setFormData((prev) => {
            const next = { ...prev };
            // Prefill gelen şasiyi chassisList'e ekle
            if (prefill.chassis && (!next.chassisList || next.chassisList.length === 0)) {
                next.chassisList = [{
                    chassisNo: prefill.chassis,
                    licensePlate: prefill.plate || ''
                }];
            }
            return next;
        });
    }, [visible, editMode, prefill, setFormData]);

    React.useEffect(() => {
        if (!visible) return;
        if (!enableCustomerTypeFeatures) return;
        if (!formData.customerType && customerTypes.length > 0) {
            setFormData((prev) => ({
                ...prev,
                customerType: customerTypes[0].value || '1'
            }));
        }
    }, [visible, enableCustomerTypeFeatures, customerTypes, formData.customerType, setFormData]);

    const isFormDisabled = isSubmitting || isDeleting;
    const processingMessage = isSubmitting ? th('common.saving') : isDeleting ? th('common.deleting') : '';

    const handleFieldChange = (field, value) => {
        if (field === 'tcNumber') {
            if (value && !/^\d*$/.test(value.toString())) return;
            if (value && value.toString().length > 11) return;
        }
        const updatedData = { ...formData, [field]: value };
        setFormData(updatedData);
    };

    const handleFieldBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const handleCountryChange = (country) => {
        const updatedData = { ...formData, country };
        setFormData(updatedData);
    };

    const handleSubmit = async () => {
        setFormSubmitted(true);
        const allFields = Object.keys(formData);
        const newTouched = {};
        allFields.forEach((field) => {
            newTouched[field] = true;
        });
        setTouched(newTouched);
        await handleSave();
    };

    const renderFieldError = (fieldName) => {
        if ((touched[fieldName] || formSubmitted.submitted) && errors[fieldName]) {
            return <small className="p-error">{errors[fieldName]}</small>;
        }
        return null;
    };

    // Şasi ekleme işlemi
    const handleAddChassis = () => {
        if (!formData.chassis) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: th('customerDefinition.selectChassisFirst'),
                life: 3000
            });
            return;
        }

        const result = addChassis(formData.chassis, formData.selectedPlate);
        
        if (!result.success) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: result.message,
                life: 3000
            });
        }
    };

    // Şasi silme template
    const chassisActionTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-trash"
                rounded
                severity="danger"
                text
                onClick={() => removeChassis(rowData.chassisNo)}
                disabled={isFormDisabled}
            />
        );
    };

    const isTcNumberRequired = formData.country?.text === 'Türkiye' || formData.country?.name === 'Türkiye';
    const isCompany = enableCustomerTypeFeatures && String(formData.customerType || '1') === '2';

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between">
            <span className="text-xl font-semibold">
                {editMode ? th('customerDefinition.editTitle') : th('customerDefinition.newTitle')}
            </span>
            {isFormDisabled && (
                <div className="flex align-items-center gap-2">
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} />
                    <span className="text-sm">{processingMessage}</span>
                </div>
            )}
        </div>
    );

    const renderFooter = () => {
        if (editMode) {
            return (
                <div className="flex justify-content-between gap-2">
                    <Button label={th('common.delete')} icon="pi pi-trash" onClick={handleDelete} loading={isDeleting} severity="danger" disabled={isSubmitting || isFormDisabled} />
                    <div className="flex gap-2">
                        <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined disabled={isFormDisabled} type="button" />
                        <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} disabled={isDeleting || isFormDisabled} type="button" />
                    </div>
                </div>
            );
        }
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined disabled={isFormDisabled} type="button" />
                <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} disabled={isFormDisabled} type="button" />
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={visible}
                style={{ width: '85%', maxWidth: '1200px' }}
                header={dialogHeader}
                modal
                className="p-fluid customer-definition-dialog"
                footer={renderFooter()}
                onHide={onHide}
                closable={!isFormDisabled}
                closeOnEscape={!isFormDisabled}
                dismissableMask={false}
                maximizable
                blockScroll
            >
                {isFormDisabled && (
                    <Message severity="info" text={processingMessage} className="w-full mb-4" icon="pi pi-spin pi-spinner" />
                )}

                <div className={`${isFormDisabled ? 'p-disabled' : ''}`}>
                    <div className="grid">

                        {enableCustomerTypeFeatures && (
                            <div className="col-12">
                                <div className="field mb-3">
                                    <label htmlFor="customerType" className="font-bold block mb-2">
                                        <i className="pi pi-users mr-1"></i>
                                        {th('customerDefinition.formLabels.customerType')}
                                    </label>
                                    <Dropdown
                                        id="customerType"
                                        value={formData.customerType}
                                        options={customerTypes}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => handleFieldChange('customerType', e.value)}
                                        onBlur={() => handleFieldBlur('customerType')}
                                        className="w-full"
                                        disabled={isFormDisabled}
                                        placeholder={th('customerDefinition.selectCustomerType')}
                                    />
                                    {renderFieldError('customerType')}
                                </div>
                            </div>
                        )}

                        <div className="col-12">
                            <h3 className="section-title mt-3">
                                <i className="pi pi-car mr-2"></i>
                                {th('customerDefinition.vehicleInfo')}
                            </h3>
                            <Divider />
                        </div>

                        {/* Şasi Ekleme Formu */}
                        <div className="col-12 md:col-5">
                            <div className="field mb-3">
                                <label htmlFor="chassis" className="font-bold block mb-2">
                                    <i className="pi pi-tag mr-1"></i>
                                    {th('customerDefinition.formLabels.chassis')}{!isCompany && '*'}
                                </label>
                                <ChassisWithExternalDropdown
                                    value={formData.chassis}
                                    onChange={(value) => handleFieldChange('chassis', value)}
                                    onBlur={() => handleFieldBlur('chassis')}
                                    className={(touched.chassis || formSubmitted) && errors.chassis ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.selectChassis')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-5">
                            <div className="field mb-3">
                                <label htmlFor="selectedPlate" className="font-bold block mb-2">
                                    <i className="pi pi-credit-card mr-1"></i>
                                    {th('customerDefinition.formLabels.plate')}
                                </label>
                                <InputText
                                    id="selectedPlate"
                                    value={formData.selectedPlate}
                                    onChange={(e) => handleFieldChange('selectedPlate', e.target.value)}
                                    className="w-full"
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.enterPlate')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-2">
                            <div className="field mb-3">
                                <label className="font-bold block mb-1">&nbsp;</label>
                                <Button
                                    label={th('customerDefinition.addChassis')}
                                    icon="pi pi-plus"
                                    onClick={handleAddChassis}
                                    disabled={isFormDisabled || !formData.chassis}
                                    className="w-full"
                                    style={{ height: '45px' }}
                                />
                            </div>
                        </div>

                        {/* Eklenen Şasiler Tablosu */}
                        <div className="col-12">
                            <div className="mb-3">
                                <div className="card-header" style={{ background: 'rgb(39, 58, 129)', color: 'white', padding: '0.75rem 1rem', borderRadius: '8px 8px 0 0' }}>
                                    <div className="flex align-items-center">
                                        <i className="pi pi-list mr-2"></i>
                                        <span className="font-semibold">{th('customerDefinition.addedChassis')}</span>
                                    </div>
                                </div>
                                <DataTable
                                    value={formData.chassisList || []}
                                    dataKey="chassisNo"
                                    className="p-datatable-sm"
                                    emptyMessage={th('customerDefinition.noChassisAdded')}
                                    responsiveLayout="scroll"
                                    size="small"
                                >
                                    <Column field="chassisNo" header={th('customerDefinition.columnHeaders.chassisNo')} style={{ width: '50%' }} />
                                    <Column field="licensePlate" header={th('customerDefinition.columnHeaders.licensePlate')} style={{ width: '35%' }} />
                                    <Column header={th('common.actions')} body={chassisActionTemplate} style={{ width: '15%', textAlign: 'left' }} />
                                </DataTable>
                                {renderFieldError('chassisList')}
                            </div>
                        </div>

                        <h3 className="section-title">
                            <i className="pi pi-user mr-2"></i>
                            {isCompany ? th('customerDefinition.formLabels.companyInfo') : th('customerDefinition.personalInfo')}
                        </h3>
                        <Divider />

                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="firstName" className="font-bold block mb-2">
                                    <i className="pi pi-user mr-1"></i>
                                    {isCompany ? th('customerDefinition.formLabels.companyName') : th('customerDefinition.formLabels.firstName')}*
                                </label>
                                <InputText
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                    onBlur={() => handleFieldBlur('firstName')}
                                    className={(touched.firstName || formSubmitted) && errors.firstName ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={isCompany ? th('customerDefinition.formLabels.enterCompanyName') : th('customerDefinition.enterFirstName')}
                                />
                                {renderFieldError('firstName')}
                            </div>
                        </div>

                        {!isCompany && (
                            <div className="col-12 md:col-6">
                                <div className="field mb-3">
                                    <label htmlFor="lastName" className="font-bold block mb-2">
                                        <i className="pi pi-user mr-1"></i>
                                        {th('customerDefinition.formLabels.lastName')}*
                                    </label>
                                    <InputText
                                        id="lastName"
                                        value={formData.lastName}
                                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                                        onBlur={() => handleFieldBlur('lastName')}
                                        className={(touched.lastName || formSubmitted) && errors.lastName ? 'p-invalid w-full' : 'w-full'}
                                        disabled={isFormDisabled}
                                        placeholder={th('customerDefinition.enterLastName')}
                                    />
                                    {renderFieldError('lastName')}
                                </div>
                            </div>
                        )}

                        {!isCompany && (
                            <div className="col-12 md:col-6">
                                <div className="field mb-3">
                                    <label htmlFor="tcNumber" className="font-bold block mb-2">
                                        <i className="pi pi-id-card mr-1"></i>
                                        {th('customerDefinition.formLabels.tcNumber')}*
                                        {isTcNumberRequired ? '*' : ''}
                                    </label>
                                    <InputNumber
                                        id="tcNumber"
                                        value={formData.tcNumber}
                                        onValueChange={(e) => handleFieldChange('tcNumber', e.value)}
                                        onBlur={() => handleFieldBlur('tcNumber')}
                                        useGrouping={false}
                                        min={0}
                                        max={99999999999}
                                        maxLength={11}
                                        className={(touched.tcNumber || formSubmitted) && (errors.tcNumber || !validateTCNumber(formData.tcNumber)) ? 'p-invalid w-full' : 'w-full'}
                                        disabled={isFormDisabled}
                                        placeholder="00000000000"
                                    />
                                    {renderFieldError('tcNumber')}
                                    {formData.tcNumber &&
                                        !validateTCNumber(formData.tcNumber) &&
                                        (touched.tcNumber || formSubmitted) && (
                                            <small className="p-error">
                                                {th('customerDefinition.invalidTCNumber', { fallback: 'Geçersiz TC kimlik numarası' })}
                                            </small>
                                        )}
                                </div>
                            </div>
                        )}

                        {isCompany && (
                            <>
                                <div className="col-12 md:col-6">
                                    <div className="field mb-3">
                                        <label htmlFor="taxNumber" className="font-bold block mb-2">
                                            <i className="pi pi-id-card mr-1"></i>
                                            {th('customerDefinition.formLabels.taxNumber')}*
                                        </label>
                                        <InputNumber
                                            id="taxNumber"
                                            value={formData.taxNumber}
                                            onValueChange={(e) => handleFieldChange('taxNumber', e.value)}
                                            onBlur={() => handleFieldBlur('taxNumber')}
                                            useGrouping={false}
                                            className={(touched.taxNumber || formSubmitted) && errors.taxNumber ? 'p-invalid w-full' : 'w-full'}
                                            disabled={isFormDisabled}
                                            placeholder={th('customerDefinition.formLabels.enterTaxNumber')}
                                        />
                                        {renderFieldError('taxNumber')}
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field mb-3">
                                        <label htmlFor="taxOffice" className="font-bold block mb-2">
                                            <i className="pi pi-briefcase mr-1"></i>
                                            {th('customerDefinition.formLabels.taxOffice')}*
                                        </label>
                                        <InputText
                                            id="taxOffice"
                                            value={formData.taxOffice}
                                            onChange={(e) => handleFieldChange('taxOffice', e.target.value)}
                                            onBlur={() => handleFieldBlur('taxOffice')}
                                            className={(touched.taxOffice || formSubmitted) && errors.taxOffice ? 'p-invalid w-full' : 'w-full'}
                                            disabled={isFormDisabled}
                                            placeholder={th('customerDefinition.formLabels.enterTaxOffice')}
                                        />
                                        {renderFieldError('taxOffice')}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="phone" className="font-bold block mb-2">
                                    <i className="pi pi-phone mr-1"></i>
                                    {th('customerDefinition.formLabels.phone')}*
                                </label>
                                <InputNumber
                                    id="phone"
                                    value={formData.phone}
                                    onValueChange={(e) => handleFieldChange('phone', e.value)}
                                    onBlur={() => handleFieldBlur('phone')}
                                    useGrouping={false}
                                    className={(touched.phone || formSubmitted) && errors.phone ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.enterPhone')}
                                />
                                {renderFieldError('phone')}
                            </div>
                        </div>

                        <div className="col-12">
                            <h3 className="section-title mt-3">
                                <i className="pi pi-map-marker mr-2"></i>
                                {th('customerDefinition.addressInfo')}
                            </h3>
                            <Divider />
                        </div>

                        <div className="col-12">
                            <div className="field mb-3">
                                <label htmlFor="address" className="font-bold block mb-2">
                                    <i className="pi pi-home mr-1"></i>
                                    {th('customerDefinition.formLabels.address')}*
                                </label>
                                <InputText
                                    id="address"
                                    value={formData.address}
                                    onChange={(e) => handleFieldChange('address', e.target.value)}
                                    onBlur={() => handleFieldBlur('address')}
                                    className={(touched.address || formSubmitted) && errors.address ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.enterAddress')}
                                />
                                {renderFieldError('address')}
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field mb-3">
                                <label htmlFor="country" className="font-bold block mb-2">
                                    <i className="pi pi-globe mr-1"></i>
                                    {th('customerDefinition.formLabels.country')}*
                                </label>
                                <CountryDropdown
                                    value={formData.country}
                                    onChange={handleCountryChange}
                                    onBlur={() => handleFieldBlur('country')}
                                    className={(touched.country || formSubmitted) && errors.country ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.selectCountry')}
                                    languageId={1}
                                />
                                {renderFieldError('country')}
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field mb-3">
                                <label htmlFor="city" className="font-bold block mb-2">
                                    <i className="pi pi-building mr-1"></i>
                                    {th('customerDefinition.formLabels.city')}
                                </label>
                                <InputText
                                    id="city"
                                    value={formData.city}
                                    onChange={(e) => handleFieldChange('city', e.target.value)}
                                    onBlur={() => handleFieldBlur('city')}
                                    className="w-full"
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.enterCity')}
                                    maxLength={100}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field mb-3">
                                <label htmlFor="postalCode" className="font-bold block mb-2">
                                    <i className="pi pi-envelope mr-1"></i>
                                    {th('customerDefinition.formLabels.postalCode')}
                                </label>
                                <InputNumber
                                    id="postalCode"
                                    value={formData.postalCode}
                                    onValueChange={(e) => handleFieldChange('postalCode', e.value)}
                                    onBlur={() => handleFieldBlur('postalCode')}
                                    useGrouping={false}
                                    disabled={isFormDisabled}
                                    placeholder={th('customerDefinition.enterPostalCode')}
                                />
                            </div>
                        </div>

                        {enableCustomerTypeFeatures && (
                            <>
                                <div className="col-12">
                                    <h3 className="section-title mt-3">
                                        <i className="pi pi-percentage mr-2"></i>
                                        {th('customerDefinition.formLabels.discountSectionTitle')}
                                    </h3>
                                    <Divider />
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field mb-3">
                                        <label htmlFor="partsDiscountRate" className="font-bold block mb-2">
                                            <i className="pi pi-cog mr-1"></i>
                                            {th('customerDefinition.formLabels.partsDiscountRate')}
                                        </label>
                                        <InputNumber
                                            id="partsDiscountRate"
                                            value={formData.partsDiscountRate}
                                            onValueChange={(e) => handleFieldChange('partsDiscountRate', e.value)}
                                            onBlur={() => handleFieldBlur('partsDiscountRate')}
                                            useGrouping={false}
                                            min={0}
                                            max={100}
                                            className={(touched.partsDiscountRate || formSubmitted) && errors.partsDiscountRate ? 'p-invalid w-full' : 'w-full'}
                                            disabled={isFormDisabled}
                                            placeholder={th('customerDefinition.formLabels.enterPartsDiscountRate')}
                                        />
                                        {renderFieldError('partsDiscountRate')}
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field mb-3">
                                        <label htmlFor="laborDiscountRate" className="font-bold block mb-2">
                                            <i className="pi pi-wrench mr-1"></i>
                                            {th('customerDefinition.formLabels.laborDiscountRate')}
                                        </label>
                                        <InputNumber
                                            id="laborDiscountRate"
                                            value={formData.laborDiscountRate}
                                            onValueChange={(e) => handleFieldChange('laborDiscountRate', e.value)}
                                            onBlur={() => handleFieldBlur('laborDiscountRate')}
                                            useGrouping={false}
                                            min={0}
                                            max={100}
                                            className={(touched.laborDiscountRate || formSubmitted) && errors.laborDiscountRate ? 'p-invalid w-full' : 'w-full'}
                                            disabled={isFormDisabled}
                                            placeholder={th('customerDefinition.formLabels.enterLaborDiscountRate')}
                                        />
                                        {renderFieldError('laborDiscountRate')}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default CustomerDefinitionDialog;
